import { useEffect, useState } from 'react';
import { ApiError } from '../../../shared/shared.api';
import { getCustomer, type CustomerDetail } from '../customers.api';

export type CustomerRecordState =
  { status: 'loading' } | { status: 'loaded'; customer: CustomerDetail } | { status: 'forbidden' } | { status: 'not_found' } | { status: 'error' };

function toErrorState(err: unknown): CustomerRecordState {
  if (err instanceof ApiError && err.status === 403) return { status: 'forbidden' };
  if (err instanceof ApiError && err.status === 404) return { status: 'not_found' };
  return { status: 'error' };
}

/** One customer, fetched by id and re-fetchable after a mutation commits. */
export function useCustomerRecord(id: string) {
  const [state, setState] = useState<CustomerRecordState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    getCustomer(id)
      .then((customer) => {
        if (!cancelled) setState({ status: 'loaded', customer });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  async function reload(): Promise<void> {
    try {
      setState({ status: 'loaded', customer: await getCustomer(id) });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  return { state, reload };
}
